import React, { useEffect, useMemo, useState } from 'react';
import { FiTrash2, FiEye, FiSearch, FiEyeOff, FiRotateCcw } from 'react-icons/fi';
import { orderBy, limit } from 'firebase/firestore';
import {
  Card,
  Table,
  Badge,
  Button,
  Input,
  Modal,
  Pagination,
  CsvExportButton,
  BulkActionBar,
} from '../../components/UI';
import { useFirestoreCollection, usePagination, useUserDirectory } from '../../hooks';
import {
  deletePost, softDeletePost, restorePost,
  logModerationAction, MODERATION_REASON_CODES,
} from '../../services/adminService';
import styles from './Posts.module.css';

// Schema (verified against production documents, 2026-08-21):
//   posts.{ userId, username, userAvatar, caption, media[{ type:'image'|'video',
//           uri }], petId, likes, commentsCount, reactions{}, isPublic,
//           isDeleted, deletedAt, hiddenByAdmin, allowComments,
//           allowMateRequests, createdAt (ISO string) }
// The old code read `content`/`text`, `mediaUrls`/`imageUrl`, `userName` and
// `likesCount` — none of which the app writes. That is why every row rendered
// "(no text)", no media badge, and a raw uid for the author.

const formatDate = (value) => {
  if (!value) return '-';
  const d = value.toDate ? value.toDate() : new Date(value);
  if (isNaN(d.getTime())) return '-';
  return d.toLocaleString();
};

/**
 * Media on a post is `[{ type: 'image' | 'video', uri }]`. Older documents (and
 * anything imported) may instead carry mediaUrls / images / imageUrl as bare
 * strings, so every shape is normalised to `{ type, uri }` here.
 */
const postMedia = (post) => {
  if (!post) return [];
  const out = [];
  const push = (entry) => {
    if (!entry) return;
    if (typeof entry === 'string') {
      out.push({ type: /\.(mp4|mov|m4v|webm)(\?|$)/i.test(entry) ? 'video' : 'image', uri: entry });
      return;
    }
    const uri = entry.uri || entry.url || entry.downloadURL;
    if (uri) out.push({ type: entry.type === 'video' ? 'video' : 'image', uri });
  };
  if (Array.isArray(post.media)) post.media.forEach(push);
  if (Array.isArray(post.mediaUrls)) post.mediaUrls.forEach(push);
  if (Array.isArray(post.images)) post.images.forEach(push);
  push(post.imageUrl);
  // The app has been observed writing the same uri twice with different
  // download tokens; dedupe on the storage path so the gallery is not doubled.
  const seen = new Set();
  return out.filter((m) => {
    const key = m.uri.split('?')[0];
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

/** Caption is the real text field; content/text are legacy fallbacks. */
const postText = (post) => post?.caption || post?.content || post?.text || '';

/** `likes` is the live counter; `likesCount` only exists on legacy documents. */
const postLikes = (post) => post?.likes ?? post?.likesCount ?? 0;

/** Emoji reactions are a map of { love: 1, laugh: 2, ... }. */
const postReactions = (post) => {
  const r = post?.reactions;
  if (!r || typeof r !== 'object') return 0;
  return Object.values(r).reduce((sum, n) => sum + (Number(n) || 0), 0);
};

const Posts = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selected, setSelected] = useState(null);
  const [showDetail, setShowDetail] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [errorMsg, setErrorMsg] = useState('');
  // visibility toggles for moderation. Hidden = soft-deleted via adminSoftDeletePost.
  const [showHidden, setShowHidden] = useState(false);

  // DSA Art. 17 structured-reason picker. Used by Hide / Delete / Bulk hide
  // / Bulk purge so every moderation action produces a categorized record
  // in `moderationDecisions`.
  const [reasonPicker, setReasonPicker] = useState(null);
  // shape: { mode: 'hide'|'delete'|'bulk_hide'|'bulk_purge', target, reasonCode, notes }
  const [reasonCode, setReasonCode] = useState('other');
  const [reasonNotes, setReasonNotes] = useState('');
  const [reasonRunning, setReasonRunning] = useState(false);

  const openReasonPicker = (mode, target = null) => {
    setReasonPicker({ mode, target });
    setReasonCode('other');
    setReasonNotes('');
  };
  const closeReasonPicker = () => {
    setReasonPicker(null);
    setReasonCode('other');
    setReasonNotes('');
  };

  const submitReason = async () => {
    if (!reasonPicker) return;
    setReasonRunning(true);
    setErrorMsg('');
    try {
      const { mode, target } = reasonPicker;
      const ids = target ? [target.id] : [...selectedIds];
      const isHide = mode === 'hide' || mode === 'bulk_hide';
      const reason = `${reasonCode}${reasonNotes ? `: ${reasonNotes}` : ''}`;

      // Underlying mutation
      if (isHide) {
        await Promise.all(ids.map((id) =>
          softDeletePost({ postId: id, restore: false, reason })
        ));
      } else {
        await Promise.all(ids.map((id) => deletePost(id, reason)));
      }

      // DSA Article 17 statement of reasons — separate ledger.
      await Promise.all(ids.map((id) =>
        logModerationAction({
          targetType: 'post',
          targetId: id,
          action: isHide ? 'hide' : 'delete',
          reasonCode,
          notes: reasonNotes || undefined,
          userId: target?.userId,
        }).catch(() => null) // best-effort; main mutation already succeeded
      ));

      if (!target) setSelectedIds([]);
      setShowDetail(false);
      setSelected(null);
      closeReasonPicker();
    } catch (err) {
      console.error(err);
      setErrorMsg(`Action failed: ${err.message}`);
    } finally {
      setReasonRunning(false);
    }
  };

  const constraints = useMemo(
    () => [orderBy('createdAt', 'desc'), limit(500)],
    []
  );
  const { data: posts, loading } = useFirestoreCollection('posts', constraints);
  const directory = useUserDirectory();

  // Full-size viewer for a single media item, opened from the row thumbnail or
  // from the detail modal's gallery.
  const [lightbox, setLightbox] = useState(null); // { uri, type }

  const filtered = useMemo(() => {
    let list = posts;
    // Hide soft-deleted posts unless the admin asks to see them.
    if (!showHidden) list = list.filter((p) => !p.hiddenByAdmin);
    if (!searchQuery) return list;
    const q = searchQuery.toLowerCase();
    return list.filter(
      (p) =>
        (p.id || '').toLowerCase().includes(q) ||
        postText(p).toLowerCase().includes(q) ||
        // Resolved account name / handle / email, not just the uid.
        directory.searchBlobOf(p.userId).includes(q) ||
        (p.username || '').toLowerCase().includes(q)
    );
  }, [posts, searchQuery, showHidden, directory]);

  const { page, pageSize, pageCount, pageData, totalItems, setPage, setPageSize } =
    usePagination(filtered, 25);

  useEffect(() => setSelectedIds([]), [searchQuery]);

  // Soft hide. Default action — reversible. Sets hiddenByAdmin: true.
  // Routes through the DSA-reason picker so every action gets a structured
  // category logged to moderationDecisions.
  const handleHide = (post) => openReasonPicker('hide', post);

  // Restore a previously soft-deleted post. Sets hiddenByAdmin: false.
  const handleRestore = async (post) => {
    if (!window.confirm(`Restore this hidden post? It will be visible to users again.`)) return;
    setActionLoading(true);
    setErrorMsg('');
    try {
      await restorePost(post.id);
      setShowDetail(false);
      setSelected(null);
    } catch (err) {
      console.error(err);
      setErrorMsg(`Restore failed: ${err.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  // Hard delete. Irreversible — removes the doc entirely. Gated behind an
  // extra confirm. Use Hide as the default; reach for this only when content
  // must be purged (e.g. illegal content). Reason picker still required.
  const handleDelete = (post) => {
    if (!window.confirm(
      'HARD DELETE — this removes the post permanently and is NOT reversible. Use "Hide" instead unless the content must be purged. Continue?'
    )) return;
    openReasonPicker('delete', post);
  };

  // Default bulk action: soft-hide. Routes through the reason picker so
  // each bulk sweep gets a single shared structured reason in
  // moderationDecisions (one entry per post).
  const bulkHide = () => {
    if (!selectedIds.length) return;
    openReasonPicker('bulk_hide');
  };

  // Hard purge — irreversible. Gated behind an extra confirm.
  const bulkPurge = () => {
    if (!selectedIds.length) return;
    if (!window.confirm(
      `HARD DELETE ${selectedIds.length} posts permanently? This is NOT reversible. Use "Hide selected" instead unless content must be purged.`
    )) return;
    openReasonPicker('bulk_purge');
  };

  const toggleRow = (id, checked) =>
    setSelectedIds((prev) => (checked ? [...new Set([...prev, id])] : prev.filter((x) => x !== id)));
  const toggleAllOnPage = (checked) => {
    const ids = pageData.map((p) => p.id);
    setSelectedIds((prev) => {
      if (checked) return [...new Set([...prev, ...ids])];
      const drop = new Set(ids);
      return prev.filter((x) => !drop.has(x));
    });
  };

  const csvColumns = [
    { key: 'id', label: 'Post ID' },
    { key: 'userId', label: 'Author UID' },
    { key: 'authorName', label: 'Author Name', accessor: (p) => directory.nameOf(p.userId, p.username || '') },
    { key: 'authorEmail', label: 'Author Email', accessor: (p) => directory.emailOf(p.userId) },
    { key: 'caption', label: 'Caption', accessor: (p) => postText(p) },
    { key: 'mediaCount', label: 'Media', accessor: (p) => postMedia(p).length },
    { key: 'likes', label: 'Likes', accessor: (p) => postLikes(p) },
    { key: 'commentsCount', label: 'Comments' },
    { key: 'reactions', label: 'Reactions', accessor: (p) => postReactions(p) },
    { key: 'isPublic', label: 'Public', accessor: (p) => (p.isPublic === false ? 'no' : 'yes') },
    { key: 'hiddenByAdmin', label: 'Hidden', accessor: (p) => (p.hiddenByAdmin ? 'yes' : 'no') },
    { key: 'createdAt', label: 'Posted', accessor: (p) => formatDate(p.createdAt) },
  ];

  const columns = [
    {
      key: 'content',
      title: 'Post',
      render: (_, row) => {
        const media = postMedia(row);
        const first = media[0];
        const text = postText(row);
        return (
          <div className={styles.postCell}>
            {first ? (
              <button
                type="button"
                className={styles.thumbBtn}
                onClick={() => setLightbox(first)}
                title="View full size"
              >
                {first.type === 'video' ? (
                  <video src={first.uri} className={styles.thumb} muted playsInline preload="metadata" />
                ) : (
                  <img src={first.uri} alt="" className={styles.thumb} />
                )}
                {media.length > 1 && <span className={styles.thumbCount}>+{media.length - 1}</span>}
              </button>
            ) : (
              <div className={styles.thumbEmpty}>—</div>
            )}
            <div className={styles.postTextWrap}>
              <div className={styles.postText}>
                {text ? text.slice(0, 120) : <span className={styles.muted}>(no caption)</span>}
              </div>
              <div className={styles.postFlags}>
                {media.length > 0 && (
                  <Badge variant="secondary">
                    {media.length} {media[0].type === 'video' && media.length === 1 ? 'video' : 'media'}
                  </Badge>
                )}
                {row.isPublic === false && <Badge variant="inactive">private</Badge>}
                {row.hiddenByAdmin && <Badge variant="danger">hidden</Badge>}
                {row.isDeleted && <Badge variant="inactive">deleted in app</Badge>}
              </div>
            </div>
          </div>
        );
      },
    },
    {
      key: 'author',
      title: 'Author',
      render: (_, row) => {
        // `username` on the post is the app's combined "owner & pet" label; the
        // account's real name comes from the users directory.
        const name = directory.nameOf(row.userId, row.username || '');
        const meta = directory.emailOf(row.userId) || directory.handleOf(row.userId) || row.userId;
        const avatar = directory.avatarOf(row.userId) || row.userAvatar;
        return (
          <div className={styles.authorCell}>
            {avatar ? (
              <img src={avatar} alt="" className={styles.authorAvatar} />
            ) : (
              <span className={styles.authorAvatarFallback}>
                {(name || '?').charAt(0).toUpperCase()}
              </span>
            )}
            <div className={styles.authorText}>
              <span className={styles.authorName}>{name || '-'}</span>
              <span className={styles.authorMeta}>{meta || ''}</span>
            </div>
          </div>
        );
      },
    },
    {
      key: 'engagement',
      title: 'Engagement',
      render: (_, row) =>
        `❤︎ ${postLikes(row)} · 💬 ${row.commentsCount ?? 0} · ✨ ${postReactions(row)}`,
    },
    { key: 'createdAt', title: 'Posted', render: (v) => formatDate(v) },
    {
      key: 'actions',
      title: '',
      width: '160px',
      render: (_, row) => (
        <div style={{ display: 'flex', gap: 6 }}>
          <Button
            variant="ghost"
            size="sm"
            icon={<FiEye />}
            onClick={() => {
              setSelected(row);
              setShowDetail(true);
            }}
            title="View detail"
          />
          {row.hiddenByAdmin ? (
            <Button
              variant="primary"
              size="sm"
              icon={<FiRotateCcw />}
              onClick={() => handleRestore(row)}
              disabled={actionLoading}
              title="Restore (unhide)"
            />
          ) : (
            <Button
              variant="ghost"
              size="sm"
              icon={<FiEyeOff />}
              onClick={() => handleHide(row)}
              disabled={actionLoading}
              title="Hide (soft delete)"
            />
          )}
          <Button
            variant="danger"
            size="sm"
            icon={<FiTrash2 />}
            onClick={() => handleDelete(row)}
            disabled={actionLoading}
            title="Hard delete (irreversible)"
          />
        </div>
      ),
    },
  ];

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div className={styles.titleRow}>
          <h1 className={styles.title}>Posts</h1>
          {!loading && <Badge variant="primary">{posts.length} loaded</Badge>}
        </div>
        <p className={styles.subtitle}>
          Moderate user posts in the social feed
          <span className={styles.liveText}> Live</span>
        </p>
      </div>

      {errorMsg && (
        <Card>
          <div style={{ color: '#f87171' }}>{errorMsg}</div>
        </Card>
      )}

      <Card>
        <div className={styles.toolbar}>
          <div className={styles.searchBox}>
            <Input
              placeholder="Search content, author, or post id..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              icon={FiSearch}
            />
          </div>
          <Button
            variant={showHidden ? 'primary' : 'ghost'}
            size="sm"
            icon={showHidden ? <FiEye /> : <FiEyeOff />}
            onClick={() => setShowHidden((s) => !s)}
            title={showHidden ? 'Hide soft-deleted posts' : 'Show soft-deleted posts'}
          >
            {showHidden ? 'Hiding hidden' : 'Show hidden'}
          </Button>
          <CsvExportButton rows={filtered} columns={csvColumns} filename="dierend-posts" />
        </div>

        <BulkActionBar selectedCount={selectedIds.length} onClear={() => setSelectedIds([])}>
          <Button variant="ghost" size="sm" icon={<FiEyeOff />} onClick={bulkHide} disabled={actionLoading}>
            Hide selected
          </Button>
          <Button variant="danger" size="sm" icon={<FiTrash2 />} onClick={bulkPurge} disabled={actionLoading}>
            Purge selected (hard)
          </Button>
        </BulkActionBar>

        <Table
          columns={columns}
          data={pageData}
          loading={loading}
          emptyMessage="No posts found"
          selectable
          selectedIds={selectedIds}
          onToggleRow={toggleRow}
          onToggleAll={toggleAllOnPage}
        />

        <Pagination
          page={page}
          pageCount={pageCount}
          pageSize={pageSize}
          totalItems={totalItems}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
        />
      </Card>

      <Modal
        isOpen={showDetail}
        onClose={() => setShowDetail(false)}
        title="Post Details"
        size="lg"
        footer={
          selected && (
            <>
              <Button variant="ghost" onClick={() => setShowDetail(false)}>
                Close
              </Button>
              <Button
                variant="danger"
                icon={<FiTrash2 />}
                onClick={() => handleDelete(selected)}
                loading={actionLoading}
              >
                Delete Post
              </Button>
            </>
          )
        }
      >
        {selected && (
          <div className={styles.detailContent}>
            <div className={styles.detailGrid}>
              <div className={styles.detailItem}>
                <span className={styles.detailLabel}>Post ID</span>
                <span className={styles.detailValue}>{selected.id}</span>
              </div>
              <div className={styles.detailItem}>
                <span className={styles.detailLabel}>Author</span>
                <span className={styles.detailValue}>
                  {directory.nameOf(selected.userId, selected.username || '') || '-'}
                </span>
              </div>
              <div className={styles.detailItem}>
                <span className={styles.detailLabel}>Author email</span>
                <span className={styles.detailValue}>
                  {directory.emailOf(selected.userId) || '-'}
                </span>
              </div>
              <div className={styles.detailItem}>
                <span className={styles.detailLabel}>Username</span>
                <span className={styles.detailValue}>
                  {directory.handleOf(selected.userId) || '-'}
                </span>
              </div>
              <div className={styles.detailItem}>
                <span className={styles.detailLabel}>Posted as</span>
                <span className={styles.detailValue}>{selected.username || '-'}</span>
              </div>
              <div className={styles.detailItem}>
                <span className={styles.detailLabel}>Author UID</span>
                <span className={styles.detailValue}>{selected.userId || '-'}</span>
              </div>
              <div className={styles.detailItem}>
                <span className={styles.detailLabel}>Posted</span>
                <span className={styles.detailValue}>{formatDate(selected.createdAt)}</span>
              </div>
              <div className={styles.detailItem}>
                <span className={styles.detailLabel}>Likes</span>
                <span className={styles.detailValue}>{postLikes(selected)}</span>
              </div>
              <div className={styles.detailItem}>
                <span className={styles.detailLabel}>Comments</span>
                <span className={styles.detailValue}>{selected.commentsCount ?? 0}</span>
              </div>
              <div className={styles.detailItem}>
                <span className={styles.detailLabel}>Reactions</span>
                <span className={styles.detailValue}>
                  {selected.reactions && Object.keys(selected.reactions).length
                    ? Object.entries(selected.reactions)
                        .map(([k, v]) => `${k} ${v}`)
                        .join(' · ')
                    : '0'}
                </span>
              </div>
              <div className={styles.detailItem}>
                <span className={styles.detailLabel}>Visibility</span>
                <span className={styles.detailValue}>
                  {selected.isPublic === false ? 'private' : 'public'}
                  {selected.hiddenByAdmin ? ' · hidden by admin' : ''}
                  {selected.isDeleted ? ' · deleted by author' : ''}
                </span>
              </div>
            </div>

            <div className={styles.section}>
              <h4>Caption</h4>
              <p style={{ whiteSpace: 'pre-wrap' }}>{postText(selected) || '(empty)'}</p>
            </div>

            {postMedia(selected).length > 0 && (
              <div className={styles.section}>
                <h4>Media ({postMedia(selected).length})</h4>
                <div className={styles.mediaGrid}>
                  {postMedia(selected).map((m, i) => (
                    <button
                      key={`${m.uri}-${i}`}
                      type="button"
                      className={styles.mediaTile}
                      onClick={() => setLightbox(m)}
                      title="View full size"
                    >
                      {m.type === 'video' ? (
                        <video src={m.uri} muted playsInline preload="metadata" />
                      ) : (
                        <img src={m.uri} alt="" />
                      )}
                      {m.type === 'video' && <span className={styles.playBadge}>▶</span>}
                    </button>
                  ))}
                </div>
                <p className={styles.muted} style={{ marginTop: 8, fontSize: 12 }}>
                  Click any tile to open it full size. Media is served from Firebase
                  Storage with the download token embedded in the URL.
                </p>
              </div>
            )}
          </div>
        )}
      </Modal>

      <Modal
        isOpen={!!reasonPicker}
        onClose={closeReasonPicker}
        title="Statement of reasons (DSA Art. 17)"
        footer={
          <>
            <Button variant="ghost" onClick={closeReasonPicker} disabled={reasonRunning}>Cancel</Button>
            <Button
              variant={reasonPicker?.mode === 'delete' || reasonPicker?.mode === 'bulk_purge' ? 'danger' : 'primary'}
              loading={reasonRunning}
              onClick={submitReason}
            >
              {reasonPicker?.mode === 'hide' && 'Hide post'}
              {reasonPicker?.mode === 'delete' && 'Hard delete'}
              {reasonPicker?.mode === 'bulk_hide' && `Hide ${selectedIds.length} posts`}
              {reasonPicker?.mode === 'bulk_purge' && `Purge ${selectedIds.length} posts`}
            </Button>
          </>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <p style={{ margin: 0, color: '#888', fontSize: 13 }}>
            DSA Article 17 requires categorical reasons for all content moderation
            decisions. The selected category lands in <code>moderationDecisions</code>
            and feeds the annual transparency report.
          </p>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: '#888' }}>Category</span>
            <select value={reasonCode} onChange={(e) => setReasonCode(e.target.value)}
              style={{ background: '#0f1218', border: '1px solid #2a2f3a', borderRadius: 6, padding: '8px 10px', color: '#e8eaed' }}>
              {MODERATION_REASON_CODES.map((c) => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </select>
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: '#888' }}>Notes (optional)</span>
            <textarea rows={3} value={reasonNotes} onChange={(e) => setReasonNotes(e.target.value)}
              placeholder="Specific quote, ticket reference, etc."
              style={{ background: '#0f1218', border: '1px solid #2a2f3a', borderRadius: 6, padding: '8px 10px', color: '#e8eaed', fontFamily: 'inherit', fontSize: 13 }} />
          </label>
        </div>
      </Modal>

      {/* Full-size media viewer. Rendered outside the detail modal so it can be
          opened straight from a table row without first opening the detail. */}
      {lightbox && (
        <div
          className={styles.lightbox}
          role="dialog"
          aria-modal="true"
          aria-label="Post media"
          onClick={() => setLightbox(null)}
        >
          <button
            type="button"
            className={styles.lightboxClose}
            onClick={() => setLightbox(null)}
            aria-label="Close"
          >
            ×
          </button>
          <div className={styles.lightboxBody} onClick={(e) => e.stopPropagation()}>
            {lightbox.type === 'video' ? (
              // eslint-disable-next-line jsx-a11y/media-has-caption
              <video src={lightbox.uri} controls autoPlay playsInline />
            ) : (
              <img src={lightbox.uri} alt="" />
            )}
            <a
              className={styles.lightboxLink}
              href={lightbox.uri}
              target="_blank"
              rel="noreferrer"
            >
              Open original in a new tab
            </a>
          </div>
        </div>
      )}
    </div>
  );
};

export default Posts;
